import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Card from "../../components/ui/Card.jsx";
import PhoneField from "../../components/ui/PhoneField.jsx";
import {
  Mail,
  User,
  ShieldCheck,
  UserPlus,
  Loader2,
  CheckCircle2,
} from "lucide-react";
import { borrowerSignUp } from "../../api/borrowerPortal/borrowerSignUp.js";
import { loginWithGoogleToken } from "../../api/borrowerPortal/googleLogin.js";
import { prepareForLogin } from "../../api/client";
import { STORAGE_KEYS } from "../../utils/constants";
import { shortlistPathForCategory } from "../../utils/sourcingCategory.js";
import { completeBorrowerLogin, hasDirectLoginPayload } from "./borrowerAuthUtils.js";
import { extractAuthPayload } from "../../utils/loginAuth.js";
import GoogleContinueButton from "../../components/auth/GoogleContinueButton.jsx";
import { isValidPhone, PHONE_ERROR_MOBILE } from "../../utils/validatePhone.js";

const LS_POST_AUTH = STORAGE_KEYS.POST_AUTH_REDIRECT;

const signUpSchema = z.object({
  fullName: z.string().min(1, "Full name is required"),
  email: z.string().email("Please enter a valid email"),
  phone: z
    .string()
    .min(1, PHONE_ERROR_MOBILE)
    .refine((v) => isValidPhone(v), { message: PHONE_ERROR_MOBILE }),
  agree: z.literal(true, {
    errorMap: () => ({ message: "You must agree to the Terms and Privacy Policy" }),
  }),
});

function getSignUpMessage(response) {
  if (!response || typeof response !== "object") return null;
  const data = response.data != null ? response.data : response;
  if (typeof data !== "object") return null;

  const msg = data.message;

  if (Array.isArray(msg) && msg.length > 1) {
    const text = msg[1];
    if (typeof text === "string" && text.trim()) return text.trim();
  }

  if (typeof msg === "string" && msg.trim()) return msg.trim();

  try {
    const raw = data._server_messages;
    if (typeof raw === "string") {
      const arr = JSON.parse(raw);
      const first = arr?.[0];
      if (typeof first === "string") {
        const parsed = JSON.parse(first);
        if (parsed?.message && typeof parsed.message === "string")
          return parsed.message.trim();
      }
    }
  } catch {
    // ignore
  }

  return null;
}

function isAlreadyRegisteredEmailError(value) {
  const s = String(value || "").toLowerCase();
  return (
    s.includes("already registered") ||
    s.includes("already exists") ||
    s.includes("user already exists") ||
    s.includes("duplicate") ||
    s.includes("email already")
  );
}

export default function BorrowerSignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [serverError, setServerError] = React.useState("");
  const [successMessage, setSuccessMessage] = React.useState("");
  const [googleLoading, setGoogleLoading] = React.useState(false);

  React.useEffect(() => {
    if (location.state?.shortlistedProducts?.length) {
      try {
        localStorage.setItem(
          STORAGE_KEYS.SHORTLIST_PENDING,
          JSON.stringify(location.state.shortlistedProducts)
        );
      } catch (err) {
        console.error("Failed to save pending shortlist", err);
      }
    }
  }, [location.state]);

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phone: "",
      agree: false,
    },
  });

  const phoneValue = watch("phone");

  const finishGoogleLogin = React.useCallback(
    async (idToken) => {
      setServerError("");
      setGoogleLoading(true);
      await prepareForLogin();
      try {
        const { data, headers, payload } = await loginWithGoogleToken(idToken, {
          role: "Borrower",
        });
        const authPayload = extractAuthPayload(data) || payload;
        const em = String(authPayload?.user || authPayload?.email || "")
          .trim()
          .toLowerCase();
        if (!em || !hasDirectLoginPayload(data)) {
          setServerError("Unexpected response from Google sign-up. Please try again.");
          return;
        }
        try {
          localStorage.setItem("ds_signup_email", em);
          localStorage.setItem(LS_POST_AUTH, shortlistPathForCategory());
        } catch { /* ignore */ }
        await completeBorrowerLogin(
          em,
          { data, headers },
          authPayload,
          true,
          navigate,
          location,
          null,
          { viaGoogle: true }
        );
      } catch (err) {
        console.error("Google signup error:", err);
        if (err?.message === "LOGIN_CREDENTIALS_MISSING") {
          setServerError("Could not establish your session. Please try again.");
          return;
        }
        setServerError(err?.message || "Google sign-up failed");
      } finally {
        setGoogleLoading(false);
      }
    },
    [navigate, location]
  );

  const onSubmit = async (data) => {
    setServerError("");
    setSuccessMessage("");
    try {
      const response = await borrowerSignUp({
        email: data.email.trim().toLowerCase(),
        full_name: data.fullName.trim(),
        phone: data.phone,
        redirect_to: "/borrower/signin",
      });

      const displayMessage = getSignUpMessage(response);
      setSuccessMessage(
        displayMessage || "Account created. Please check your email for verification."
      );

      try {
        localStorage.setItem("ds_signup_email", data.email.trim().toLowerCase());
      } catch { /* ignore */ }

      try {
        const params = new URLSearchParams(location.search);
        const next = params.get("next") || location.state?.next;
        if (next) {
          localStorage.setItem(LS_POST_AUTH, next);
        } else {
          localStorage.setItem(LS_POST_AUTH, shortlistPathForCategory());
        }
      } catch { /* ignore */ }
    } catch (err) {
      const errData = err?.data ?? err?.response?.data;
      const displayError = getSignUpMessage(errData) || err?.message || "Sign up failed.";
      if (isAlreadyRegisteredEmailError(displayError)) {
        setError("email", {
          type: "server",
          message: "This email is already registered. Please sign in instead.",
        });
      }
      setServerError(displayError);
    }
  };

  const goToSignIn = () => {
    navigate("/borrower/signin", { replace: true });
  };

  const formBusy = isSubmitting || googleLoading;

  return (
    <>
      <div className="min-h-screen flex flex-col md:grid md:grid-cols-2 bg-slate-50">
        <div className="hidden md:flex relative items-center overflow-hidden pl-10">
          <div className="absolute inset-0 bg-gradient-to-b from-sky-500 to-sky-600" />
          <div className="relative z-10 text-white max-w-xl">
            <div className="flex items-center gap-2">
              <a href="/">
                <img
                  src="/dynesourcer-logo.svg"
                  alt="Dynesourcer"
                  className="h-64 md:h-96 select-none hover:opacity-90 transition-opacity"
                  draggable="false"
                  loading="eager"
                  width="384"
                  height="384"
                />
              </a>
              <div>
                <h1 className="text-4xl font-extrabold">Dynesourcer</h1>
                <p className="text-white/80">Borrower Application Portal</p>
              </div>
            </div>
            <div className="mt-10">
              <h2 className="text-3xl font-bold">Create your account</h2>
              <p className="mt-2 text-white/85">
                Shortlist products, apply online, and track progress — all in one place.
              </p>
            </div>
          </div>
        </div>

        <div className="md:hidden bg-gradient-to-b from-sky-500 to-sky-600 px-6 py-6 text-white">
          <div className="flex items-center gap-3 mb-4">
            <a href="/">
              <img
                src="/dynesourcer-logo.svg"
                alt="Dynesourcer"
                className="h-24 w-auto"
                draggable="false"
                loading="eager"
                width="96"
                height="96"
              />
            </a>
            <div>
              <h1 className="text-xl font-extrabold">Dynesourcer</h1>
              <p className="text-white/85 text-xs">Borrower Portal</p>
            </div>
          </div>
          <h2 className="text-lg font-bold">Create your account</h2>
          <p className="text-white/85 text-sm mt-1">
            Shortlist products, apply online, and track progress.
          </p>
        </div>

        <div className="flex items-center justify-center p-4 sm:p-6 md:p-6 min-h-0 flex-1 overflow-y-auto">
          <Card className="w-full max-w-md rounded-2xl bg-white p-4 sm:p-6 shadow-sm mx-auto">
            {successMessage ? (
              <div className="flex flex-col items-center text-center py-4 sm:py-6">
                <div className="rounded-full bg-emerald-100 p-4 mb-4" aria-hidden>
                  <CheckCircle2 className="h-12 w-12 text-emerald-600" />
                </div>
                <h2 className="text-lg sm:text-xl font-semibold text-slate-900">Email confirmation</h2>
                <p className="mt-3 text-sm sm:text-base text-slate-700 max-w-sm font-medium">
                  {successMessage}
                </p>
                <p className="mt-2 text-xs sm:text-sm text-slate-500">
                  Check your email for a verification link to set your password and sign in.
                </p>
                <button
                  type="button"
                  onClick={goToSignIn}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white hover:bg-sky-700 transition-colors"
                >
                  Go to Sign in
                </button>
              </div>
            ) : (
              <>
                <div className="mb-4 sm:mb-6">
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-sky-500" />
                    <h2 className="text-lg sm:text-xl font-semibold text-slate-900">
                      Create Borrower Account
                    </h2>
                  </div>
                  <p className="mt-1 text-xs sm:text-sm text-slate-600">
                    You will receive an email to verify your account and set your password.
                  </p>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 sm:space-y-4">
                  <label className="block">
                    <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">
                      Full Name
                    </span>
                    <div className="relative">
                      <input
                        type="text"
                        {...register("fullName")}
                        className={`w-full rounded-xl border px-3 py-2.5 sm:py-2.5 pr-10 text-sm sm:text-base focus:ring-2 focus:ring-sky-200 ${
                          errors.fullName ? "border-rose-300 focus:border-rose-300" : "border-slate-300"
                        }`}
                        placeholder="Jane Borrower"
                        disabled={formBusy}
                      />
                      <User className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    {errors.fullName && (
                      <p className="mt-1 text-xs text-rose-600">{errors.fullName.message}</p>
                    )}
                  </label>

                  <label className="block">
                    <span className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">
                      Email
                    </span>
                    <div className="relative">
                      <input
                        type="email"
                        {...register("email")}
                        className={`w-full rounded-xl border px-3 py-2.5 sm:py-2.5 pr-10 text-sm sm:text-base focus:ring-2 focus:ring-sky-200 ${
                          errors.email ? "border-rose-300 focus:border-rose-300" : "border-slate-300"
                        }`}
                        placeholder="you@example.com"
                        disabled={formBusy}
                      />
                      <Mail className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    </div>
                    {errors.email && (
                      <p className="mt-1 text-xs text-rose-600">{errors.email.message}</p>
                    )}
                  </label>

                  <div className="block">
                    <label htmlFor="signup-phone" className="mb-1 block text-xs sm:text-sm font-medium text-slate-700">
                      Phone Number
                    </label>
                    <PhoneField
                      id="signup-phone"
                      value={phoneValue}
                      onChange={(next) => setValue("phone", next, { shouldValidate: true, shouldTouch: true })}
                      disabled={formBusy}
                    />
                  </div>

                  <div className="block">
                    <label className="inline-flex items-start gap-2 text-xs sm:text-sm text-slate-700">
                      <input
                        type="checkbox"
                        {...register("agree")}
                        disabled={formBusy}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span>
                        I agree to the{" "}
                        <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                          Terms and Privacy Policy
                        </a>
                      </span>
                    </label>
                    {errors.agree && (
                      <p className="mt-1 text-xs text-rose-600">{errors.agree.message}</p>
                    )}
                  </div>

                  {serverError && <p className="text-xs sm:text-sm text-rose-600">{serverError}</p>}

                  <button
                    type="submit"
                    disabled={formBusy || !isValidPhone(phoneValue)}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-sky-500 px-4 py-2.5 text-sm sm:text-base font-semibold text-white hover:bg-sky-600 disabled:opacity-70 transition-colors"
                  >
                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                    {isSubmitting ? "Creating account..." : "Create account"}
                  </button>
                </form>

                <div className="relative my-4 sm:my-5">
                  <div className="absolute inset-0 flex items-center" aria-hidden>
                    <div className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase tracking-wide">
                    <span className="bg-white px-3 text-slate-400">or</span>
                  </div>
                </div>

                <div className="flex flex-col items-center gap-2 w-full">
                  <GoogleContinueButton
                    disabled={formBusy}
                    onCredential={(credential) => finishGoogleLogin(credential)}
                    onError={setServerError}
                  />
                  <p className="text-[11px] sm:text-xs text-slate-500 text-center leading-snug max-w-sm">
                    By continuing with Google, you agree to our{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 hover:underline">
                      Terms and Privacy Policy
                    </a>
                    .
                  </p>
                  {googleLoading && (
                    <p className="text-xs text-slate-500 inline-flex items-center gap-1.5">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Completing Google sign-up…
                    </p>
                  )}
                </div>

                <p className="mt-4 text-center text-xs sm:text-sm text-slate-600">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/borrower/signin")}
                    className="font-semibold text-sky-700 hover:underline"
                    disabled={formBusy}
                  >
                    Sign in
                  </button>
                </p>
              </>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
